import { FC, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import logos from '../../assets/logos';
import InformationBanner from '../../components/InformationBanner';
import { IInstallationError } from '../../models';
import { installationSelectors } from '../../redux/slices/InstallationSlice';
import { errorActions, errorCodes } from '../../static/constants';
import lang from '../../static/lang/en-NZ/index';
import { ErrorBanner } from '../ErrorBanner';
import { IErrorBannerModel } from '../ErrorBanner/ErrorBannerModel';
import './Overview.css';

export interface IOverviewProps {
    title: string;
    freeTrialTag?: React.ReactNode;
    type: string;
    tags: string[];
    blurb: ReactNode;
    error?: string;
    onSettingsClick?: (() => void) | null;
    actions?: JSX.Element;
    children: ReactNode;
}

const Overview: FC<IOverviewProps> = (props) => {
    const installation = useSelector(installationSelectors.installation);

    const integrationLang: any = lang[installation.integrationType];

    const renderErrors = () =>
        installation.errors
            ? installation.errors.map((e, i) => {
                  return <ErrorBanner key={e.code + e.action} error={getErrorBannerModel(e)} />;
              })
            : null;

    const getErrorBannerModel = (error: IInstallationError): IErrorBannerModel => {
        const errors = integrationLang.ERROR;

        let message;
        let actionButtonText = '';
        let actionButtonOnClickAction;
        let actionButtonHref = '';

        switch (error.code) {
            case errorCodes.BANK_ACCOUNT_NOT_FOUND:
                message = errors.BANK_ACCOUNT_NOT_FOUND.MESSAGE;
                actionButtonText = errors.BANK_ACCOUNT_NOT_FOUND.ACTION;
                actionButtonHref = errors.BANK_ACCOUNT_NOT_FOUND.ACTION_URL;
                break;
            case errorCodes.CHART_OF_ACCOUNT_NOT_FOUND:
                message = errors.CHART_OF_ACCOUNT_NOT_FOUND.MESSAGE;
                actionButtonText = errors.CHART_OF_ACCOUNT_NOT_FOUND.ACTION;
                actionButtonHref = errors.CHART_OF_ACCOUNT_NOT_FOUND.ACTION_URL;
                break;
            case errorCodes.TAX_RATE_NOT_FOUND:
                message = errors.TAX_RATE_NOT_FOUND.MESSAGE;
                actionButtonText = errors.TAX_RATE_NOT_FOUND.ACTION;
                actionButtonHref = errors.TAX_RATE_NOT_FOUND.ACTION_URL;
                break;
            case errorCodes.TAX_RATE_INCORRECT:
                message = errors.TAX_RATE_INCORRECT.MESSAGE;
                actionButtonText = errors.TAX_RATE_INCORRECT.ACTION;
                actionButtonHref = errors.TAX_RATE_INCORRECT.ACTION_URL;
                break;
            case errorCodes.MISSING_TRANSACTION_TYPE_MAPPING:
                message = errors.MISSING_TRANSACTION_TYPE_MAPPING.MESSAGE;
                actionButtonText = errors.MISSING_TRANSACTION_TYPE_MAPPING.ACTION;
                actionButtonHref = errors.MISSING_TRANSACTION_TYPE_MAPPING.ACTION_URL;
                break;
            case errorCodes.CLEARING_ACCOUNT_NOT_FOUND:
                message = errors.CLEARING_ACCOUNT_NOT_FOUND.MESSAGE;
                actionButtonText = errors.CLEARING_ACCOUNT_NOT_FOUND.ACTION;
                actionButtonHref = errors.CLEARING_ACCOUNT_NOT_FOUND.ACTION_URL;
                break;
            case errorCodes.CONTACT_NOT_FOUND:
                message = errors.CONTACT_NOT_FOUND.MESSAGE;
                actionButtonText = errors.CONTACT_NOT_FOUND.ACTION;
                actionButtonHref = errors.CONTACT_NOT_FOUND.ACTION_URL;
                break;
            case errorCodes.INVOICE_LIMIT_REACHED:
                message = errors.INVOICE_LIMIT_REACHED.MESSAGE;
                actionButtonText = errors.INVOICE_LIMIT_REACHED.ACTION;
                actionButtonHref = errors.INVOICE_LIMIT_REACHED.ACTION_URL;
                break;
            case errorCodes.CONNECTION_ERROR:
            case errorCodes.ERROR_CONNECTION:
            case errorCodes.FORBIDDEN:
            case errorCodes.UNAUTHORIZED:
                message =
                    errors.CONNECTION_ERROR?.MESSAGE ??
                    lang.ERROR.CONNECTION_ERROR.replace('integrationName', installation.displayName);
                break;
            case errorCodes.BILLING_SUBSCRIPTION_CANCELLED:
                message = errors.BILLING_SUBSCRIPTION_CANCELLED.MESSAGE;
                actionButtonText = errors.BILLING_SUBSCRIPTION_CANCELLED.ACTION;
                actionButtonHref = installation.billing?.url ?? '';
                break;
            case errorCodes.FREE_TRIAL_EXPIRED:
                message = errors.FREE_TRIAL_EXPIRED.MESSAGE;
                actionButtonText = errors.FREE_TRIAL_EXPIRED.ACTION;
                actionButtonHref = installation.billing?.url ?? '';
                break;
            case errorCodes.INSUFFICIENT_USER_PERMISSIONS:
                message = errors.INSUFFICIENT_USER_PERMISSIONS.MESSAGE;
                actionButtonText = errors.INSUFFICIENT_USER_PERMISSIONS.ACTION;
                actionButtonHref = errors.INSUFFICIENT_USER_PERMISSIONS.ACTION_URL;
                break;
            default:
                message = lang.ERROR.UNKNOWN_ERROR;
                break;
        }

        switch (error.action) {
            case errorActions.UPDATE_INSTALLATION_SETTINGS:
                actionButtonOnClickAction = props.onSettingsClick ?? null;
                break;
            default:
                actionButtonOnClickAction = null;
                break;
        }

        return {
            message,
            actionButtonText,
            actionButtonOnClickAction,
            actionButtonHref,
        };
    };

    const renderError = () =>
        props.error ? (
            <InformationBanner
                bannerMessage={<span className='xui-text-emphasis'>{props.error}</span>}
                sentiment={'negative'}
                displayWarningIcon={true}
            />
        ) : null;

    const type = props.type;

    const pngLogoSrc = logos[`${type}_OVERVIEW`] || logos[type];
    const svgLogo = logos[`${type}_SVG`] && logos[`${type}_SVG`]();
    const logo = pngLogoSrc ? <img src={logos[`${type}_OVERVIEW`] || logos[type]} /> : svgLogo;

    return (
        <div className='xui-u-fullwidth'>
            <div className='xui-padding-horizontal'>{installation.errors ? renderErrors() : renderError()}</div>
            <div className='xui-column-12-of-12 xui-column-6-of-12-small-up xui-column-4-of-12-large-up xui-padding-horizontal'>
                <div className={`logo-${type.toLowerCase()}`}>{logo}</div>
                {props.actions}
            </div>
            <div className='xui-column-12-of-12 xui-column-6-of-12-small-up xui-column-8-of-12-large-up xui-padding-horizontal'>
                <div className='details'>
                    <div
                        data-automationid='overview-title'
                        className='xui-padding-bottom xui-padding-top-xsmall overview-title'
                    >
                        <span className='xui-heading-2xlarge'>{props.title}</span>
                        {props.freeTrialTag}
                    </div>
                    <div>
                        <div className='xui-font-size-small xui-text-secondary'>{props.tags.join(', ')}</div>
                    </div>
                    <div className='overview-blurb'>{props.blurb}</div>
                    <div className='xui-margin-top-4xlarge'>{props.children}</div>
                </div>
            </div>
        </div>
    );
};

export default Overview;
